import { getMyLocations } from '@jq4race/api-client';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useActiveLocationId } from '../../modules/app-shell/lib/active-location-store';
import { browserTimeZone } from '../format/zoned-time';

/**
 * Resolves the IANA timezone to render timestamps in.
 *
 * Reuses the role-scoped `['my-locations']` query (already fetched by the
 * tickets and analytics screens, so it costs no extra request): org_admin sees
 * every location, a supervisor their assigned ones.
 *
 * - `timeZoneFor(locationId)` — the ticket's own location. Falls back to the
 *   browser timezone while loading or when the location is not in the list
 *   (e.g. deactivated); never to UTC.
 * - `activeTimeZone` — for org-wide views whose rows carry no location (audit
 *   log, dashboard feed): the supervisor's active location when one is
 *   selected, otherwise the browser timezone.
 */
export function useLocationTimeZones() {
  const { data } = useQuery({
    queryKey: ['my-locations'],
    queryFn: () => getMyLocations(),
  });
  const activeLocationId = useActiveLocationId();
  const locations = data?.locations;

  const timeZoneFor = useCallback(
    (locationId: string | null | undefined): string => {
      const tz = locationId ? locations?.find((l) => l.id === locationId)?.timezone : undefined;
      return tz || browserTimeZone();
    },
    [locations],
  );

  return { timeZoneFor, activeTimeZone: timeZoneFor(activeLocationId) };
}
